import { validationData } from "../../assets/validations/validationRegex";
import { useSubVetForm } from "../../context/SubVetFormContext";
import { useEnfermedad } from "../../hooks/useEnfermedades";
import { Diagnostico, getEmptyDiagnostico } from "../../interfaces/forms/Diagnostico";
import { filterBySynonyms } from "../../utils/CustomSearch";
import { today } from "../../utils/DateUtilities";
import { Option, fechasEstimadas } from "../../utils/Form";
import { capitalizeFirstLetter } from "../../utils/General";
import { MAX_TEXT_LENGTH, sanitizeTextInput } from "../../utils/textSanitizer";
import Continue from "../Continue";
import FormItem from "../FormItem";
import FormSelectOrCreate from "../FormSelectOrCreate";
import FormSelectPopover from "../FormSelectPopover";
import Title from "../Title";
import "./DiagnosticoForm.scss";
import { DiagnosticoFormProps } from "./types";
import {
  IonItem,
  IonLabel,
  IonCheckbox,
  IonCol,
  IonGrid,
  IonRow,
  IonButton,
  IonTextarea,
  isPlatform,
} from "@ionic/react";
import moment from "moment";
import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";

const DiagnosticoForm: React.FC<DiagnosticoFormProps> = ({ goBack, goForward }) => {
  const { diagnostico, setDiagnostico, diagnosticosAlternativos, setDiagnosticosAlternativos } =
    useSubVetForm();
  const { searchableEnfermedades, isLoading } = useEnfermedad();
  const { handleSubmit, control, errors, setValue, watch } = useForm<Diagnostico>({
    defaultValues: diagnostico ?? getEmptyDiagnostico(),
  });
  const formMethods = { control, errors, setValue };
  const textareaRef = useRef<HTMLIonTextareaElement>(null);

  const diagnosticoField = watch("diagnostico");
  const fechaEstimada = watch("fechaEstimada");
  const congenito = watch("congenito");
  const tratamientoPorVida = watch("tratamientoPorVida");
  const enfermedadBase = watch("enfermedadBase");
  const motivo = watch("motivo");

  const [option, setOption] = useState<Option | null>();
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);

  const onSubmit = (values: Diagnostico) => {
    setDiagnostico(values);
    goForward();
  };

  useEffect(() => {
    motivo && setOption({ label: motivo, value: "" });
  }, [motivo]);

  useEffect(() => {
    // Concatenamos todos los labels en una única cadena
    const concatenatedLabels = diagnosticosAlternativos.map((item) => item.label).join(", ");

    setValue("diagnostico", concatenatedLabels);
  }, [diagnosticosAlternativos]);

  //Reset campos si no existe diagnosticoField
  useEffect(() => {
    if (!diagnosticoField || !diagnosticosAlternativos.length) {
      const emptyDiagnostico = getEmptyDiagnostico();
      Object.keys(emptyDiagnostico).forEach((key) => {
        setValue(key as keyof Diagnostico, emptyDiagnostico[key]);
      });
    }
  }, [diagnosticoField]);

  //Hide checkboxes en movil cuando se da focus a textarea de motivoConsulta
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (textareaRef.current && !textareaRef.current.contains(event.target)) {
        setIsCollapsed(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="diagnostico-form ion-margin">
      <form onSubmit={handleSubmit(onSubmit)}>
        <IonGrid className="ion-no-padding">
          <Title title="Diagnóstico presuntivo" />

          <IonRow>
            <IonCol>
              {/* Diagnostico */}
              <FormSelectOrCreate
                creatable={true}
                options={searchableEnfermedades}
                enableMulti={{
                  isEnabled: true,
                  state: diagnosticosAlternativos,
                  setter: setDiagnosticosAlternativos,
                }}
                filterOption={(candidate, inputValue) => filterBySynonyms(candidate, inputValue)}
                isLoading={isLoading}
                formMethods={formMethods}
                fieldProps={{
                  name: "diagnostico",
                  placeholder: "Seleccione una enfermedad",
                }}
                validationProps={{
                  errorMessage: "Por favor ingrese una enfermedad",
                  validate: (value: string) =>
                    !value ||
                    value.length <= 255 ||
                    "El diagnóstico no puede superar los 255 caracteres. Seleccione menos enfermedades.",
                }}
              />
            </IonCol>
          </IonRow>

          {diagnosticoField && (
            <>
              <IonRow>
                {/* <IonCol hidden> */}
                {/* Fecha estimada Id*/}
                {/* <FormInput
                    formMethods={formMethods}
                    fieldProps={{
                      name: "fechaEstimadaId",
                      label: "",
                    }}
                  />
                </IonCol> */}

                <IonCol>
                  {/* Fecha estimada */}
                  <FormSelectPopover
                    formMethods={formMethods}
                    options={fechasEstimadas}
                    fieldProps={{
                      name: "fechaEstimada",
                      label: "Fecha estimada de origen de enfermedad",
                    }}
                    validationProps={{
                      errorMessage: "Por favor seleccione una fecha estimada",
                    }}
                  />
                </IonCol>
              </IonRow>
            </>
          )}

          <div hidden={(isCollapsed && isPlatform("hybrid")) || !diagnosticosAlternativos.length}>
            {fechaEstimada && (
              <>
                <IonRow className="ion-margin-top">
                  <IonCol className="diagnostico-form__subtitle">
                    Marque si corresponde alguna de las siguientes opciones
                  </IonCol>
                </IonRow>
                <IonRow>
                  <IonCol>
                    {/* Congenito */}
                    <IonItem lines="inset" className="ion-no-padding">
                      <IonLabel>¿Es congénito?</IonLabel>
                      <FormItem
                        render={({ name, value }) => (
                          <IonCheckbox
                            mode="md"
                            checked={value}
                            name={name}
                            value={value}
                            onIonChange={(e) => setValue("congenito", !congenito)}
                          />
                        )}
                        name="congenito"
                        control={control}
                      />
                    </IonItem>
                  </IonCol>
                </IonRow>

                <IonRow>
                  <IonCol>
                    {/* Tratamiento de por vida */}
                    <IonItem lines="inset" className="ion-no-padding">
                      <IonLabel>¿Requiere tratamiento de por vida?</IonLabel>
                      <FormItem
                        render={({ name, value }) => (
                          <IonCheckbox
                            mode="md"
                            checked={value}
                            name={name}
                            value={value}
                            onIonChange={(e) => setValue("tratamientoPorVida", !tratamientoPorVida)}
                          />
                        )}
                        name="tratamientoPorVida"
                        control={control}
                      />
                    </IonItem>
                  </IonCol>
                </IonRow>

                <IonRow>
                  <IonCol>
                    {/* Enfermedad de base */}
                    <IonItem lines="inset" className="ion-no-padding">
                      <IonLabel>¿Responde a una enfermedad de base?</IonLabel>
                      <FormItem
                        render={({ name, value }) => (
                          <IonCheckbox
                            mode="md"
                            checked={value}
                            value={value}
                            name={name}
                            onIonChange={(e) => setValue("enfermedadBase", !enfermedadBase)}
                          />
                        )}
                        name="enfermedadBase"
                        control={control}
                      />
                    </IonItem>
                  </IonCol>
                </IonRow>
              </>
            )}

            {enfermedadBase && (
              <>
                <IonRow className="ion-margin-top">
                  <IonCol>
                    {/* Motivo */}
                    <FormSelectOrCreate
                      creatable={true}
                      options={searchableEnfermedades}
                      option={option}
                      filterOption={(candidate, inputValue) =>
                        filterBySynonyms(candidate, inputValue)
                      }
                      formMethods={formMethods}
                      fieldProps={{
                        name: "motivo",
                        placeholder: "Seleccione enfermedad de base",
                      }}
                      validationProps={{
                        errorMessage: "Por favor seleccione una enfermedad de base o motivo",
                        validate: (value: string) =>
                          !value || value.length <= 255 || "Máximo 255 caracteres",
                      }}
                    />
                  </IonCol>
                </IonRow>
              </>
            )}
          </div>

          {fechaEstimada && diagnosticosAlternativos.length !== 0 && (
            <div className="ion-margin-vertical">
              {/* Motivo consulta*/}
              <IonRow>
                <IonCol className="diagnostico-form__subtitle">
                  ¿Desea agregar más información sobre la consulta?
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol>
                  <FormItem
                    render={({ onChange, value }) => (
                      <IonItem mode="ios" className="ion-no-padding">
                        <IonLabel position="floating">Breve descripción</IonLabel>
                        <IonTextarea
                          onIonChange={(event) =>
                            onChange(sanitizeTextInput(event.detail.value ?? ""))
                          }
                          value={capitalizeFirstLetter(value)}
                          ref={textareaRef}
                          onFocus={() => setIsCollapsed((prevState) => !prevState)}
                          maxlength={MAX_TEXT_LENGTH}
                          rows={6}
                          placeholder={`Ejemplo: la mascota toma anticonvulsivos desde el año ${moment(
                            today
                          ).format("YYYY")} porque es epiléptica.`}
                        />
                      </IonItem>
                    )}
                    name="motivoConsulta"
                    control={control}
                    // errors={errors}
                    rules={{
                      pattern: validationData,
                      maxLength: { value: MAX_TEXT_LENGTH, message: "Máximo 255 caracteres" },
                      validate: (value: string) =>
                        value === sanitizeTextInput(value) ||
                        "El campo contiene caracteres no permitidos",
                    }}
                  />
                </IonCol>
              </IonRow>
            </div>
          )}

          <div className="ion-margin-top">
            <IonButton onClick={goBack} mode="ios" expand="block" fill="outline" color="dark">
              Regresar
            </IonButton>
            <Continue />
          </div>
        </IonGrid>
      </form>
    </div>
  );
};

export default DiagnosticoForm;
